import type { FeatureFlagName, FeatureFlags } from "@/hooks/useFeatureFlags";
import { isHiddenInMvp } from "@/shared/lib/launch-mode";

/**
 * Состав главного меню и правила его видимости.
 *
 * Вынесено из `header.tsx` отдельным модулем, чтобы правила («гостю не
 * показываем защищённое», «раздел за флагом не показываем, пока флаг не
 * пришёл») можно было проверить тестом, не поднимая весь хедер с анимациями.
 */
export type NavItem = {
  __key: string;
  href: string;
  tauriHidden?: boolean;
  authOnly?: boolean;
  labelFallback?: string;
  /** Пункт виден только при включённом фича-флаге. */
  featureFlag?: FeatureFlagName;
};

export const allNavigation: NavItem[] = [
  { __key: "links.home", href: "/", tauriHidden: true },
  // «О нас» убрана из хедера — остаётся только в футере (footer.tsx).
  // dev_task fix-header-landing-repeating-animations.
  { __key: "links.tournaments", href: "/tournaments" },
  { __key: "links.scrims", href: "/scrims" },
  // D1: публичный раздел «Команды»
  { __key: "links.teams", href: "/rosters" },
  { __key: "links.lfg", href: "/lfg" },
  { __key: "links.organizations", href: "/organizations" },
  // Инструменты подготовки к играм (KAN-835). authOnly: раздел лежит в
  // (protected), и гостя ссылка молча выбросила бы на /login.
  // featureFlag (KAN-925): раздел скрыт с глаз до готовности.
  {
    __key: "links.tools",
    href: "/tools",
    authOnly: true,
    featureFlag: "tools",
  },
  // TODO: вернуть при реализации subscription-system
  // { __key: "links.pricing", href: "/pricing" },
  // { __key: "links.docs", href: "/docs" },
  { __key: "links.download", href: "/download", tauriHidden: true },
];

export type NavVisibilityContext = {
  isTauriApp: boolean;
  isAuthenticated: boolean;
  flags: FeatureFlags;
  /** Состояние флагов ещё неизвестно — пункт за флагом не показываем. */
  flagsLoading: boolean;
};

/**
 * Пункт за фича-флагом на время загрузки СКРЫТ, а не показан: иначе он мигнёт
 * в меню и пропадёт, как только придёт ответ с выключенным флагом. Для
 * маршрута правило обратное — см. `ToolsFeatureGate`.
 */
export function getVisibleNavigation(ctx: NavVisibilityContext): NavItem[] {
  return allNavigation
    .filter((item) => !ctx.isTauriApp || !item.tauriHidden)
    .filter((item) => !isHiddenInMvp(item.href))
    .filter((item) => !item.authOnly || ctx.isAuthenticated)
    .filter(
      (item) =>
        !item.featureFlag ||
        (!ctx.flagsLoading && ctx.flags[item.featureFlag] === true),
    );
}
